import type { ReactNode } from 'react';
import { AlertDialog, Dialog as RDialog } from 'radix-ui';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { Button } from './Button';

const overlay = 'fixed inset-0 z-40 bg-[var(--overlay)] animate-[tp-fade-in_120ms_ease-out]';
const content =
  'fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col ' +
  'rounded-[var(--radius-panel)] border border-line bg-surface shadow-[var(--shadow-pop)] animate-[tp-pop-in_140ms_ease-out] focus:outline-none';

const widths = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' } as const;

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: keyof typeof widths;
  /** Prevent closing by outside click (e.g. long forms). */
  modalLock?: boolean;
  /** False: no close button and Escape does nothing (the footer's actions are the only way out). */
  dismissible?: boolean;
}

export function Dialog({ open, onOpenChange, title, description, children, footer, size = 'md', modalLock, dismissible = true }: DialogProps) {
  const { t } = useTranslation();
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className={overlay} />
        <RDialog.Content
          className={cn(content, widths[size])}
          onPointerDownOutside={modalLock || !dismissible ? (e) => e.preventDefault() : undefined}
          onEscapeKeyDown={dismissible ? undefined : (e) => e.preventDefault()}
          aria-describedby={description ? undefined : undefined}
        >
          <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <RDialog.Title className="text-base font-semibold text-ink">{title}</RDialog.Title>
              {description ? (
                <RDialog.Description className="mt-1 text-sm text-ink-2">{description}</RDialog.Description>
              ) : (
                <RDialog.Description className="sr-only">{typeof title === 'string' ? title : ''}</RDialog.Description>
              )}
            </div>
            {dismissible && (
              <RDialog.Close asChild>
                <button
                  type="button"
                  className="-mr-1 rounded-[var(--radius-ctl)] p-1 text-ink-3 hover:bg-surface-3 hover:text-ink"
                  aria-label={t('common.close')}
                >
                  <X className="size-4" />
                </button>
              </RDialog.Close>
            )}
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</footer>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  loading?: boolean;
  onConfirm: () => void;
  children?: ReactNode;
}

export function ConfirmDialog({
  open, onOpenChange, title, description, confirmLabel, tone = 'danger', loading, onConfirm, children,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className={overlay} />
        <AlertDialog.Content className={cn(content, widths.sm)}>
          <div className="px-5 pt-5">
            <AlertDialog.Title className="text-base font-semibold">{title}</AlertDialog.Title>
            {description && <AlertDialog.Description className="mt-2 text-sm text-ink-2">{description}</AlertDialog.Description>}
            {children && <div className="mt-3">{children}</div>}
          </div>
          <div className="mt-5 flex justify-end gap-2 border-t border-line px-5 py-3">
            <AlertDialog.Cancel asChild>
              <Button variant="ghost">{t('common.cancel')}</Button>
            </AlertDialog.Cancel>
            <Button
              variant={tone === 'danger' ? 'danger' : 'primary'}
              loading={loading}
              onClick={(e) => {
                e.preventDefault();
                onConfirm();
              }}
            >
              {confirmLabel}
            </Button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

/** Side sheet (mobile navigation). */
export function Sheet({ open, onOpenChange, title, children }: { open: boolean; onOpenChange: (o: boolean) => void; title: string; children: ReactNode }) {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className={overlay} />
        <RDialog.Content className="fixed inset-y-0 left-0 z-50 flex w-[min(20rem,88vw)] flex-col overflow-y-auto border-r border-line bg-surface shadow-[var(--shadow-pop)] animate-[tp-slide-in_160ms_ease-out] focus:outline-none">
          <RDialog.Title className="sr-only">{title}</RDialog.Title>
          <RDialog.Description className="sr-only">{title}</RDialog.Description>
          {children}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}
